import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Chip,
  Avatar
} from '@mui/material';
import {
  Group as GroupIcon,
  Star as StarIcon,
  AdminPanelSettings as DelegateIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teamsService } from '../../services/api';

const TeamSelector = ({ onTeamChange }) => {
  const { currentUser } = useAuth();
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadAvailableTeams();
    }
  }, [currentUser]);

  const loadAvailableTeams = async () => {
    try {
      setLoading(true);
      
      if (currentUser.role === 'SUPER_ADMIN') {
        // SuperAdmin voit toutes les équipes
        const allTeams = await teamsService.getAll();
        const teamsWithRole = allTeams.map(team => ({
          ...team,
          userRole: 'SUPER_ADMIN',
          isMainTeam: false,
          isDelegated: false
        }));
        setAvailableTeams(teamsWithRole);
        if (teamsWithRole.length > 0) {
          setSelectedTeamId(teamsWithRole[0].id);
        }
      } else {
        // Admin/Viewer : équipe principale + délégations
        const managedTeams = await teamsService.getManagedTeams(currentUser.id);
        setAvailableTeams(managedTeams);
        
        // Sélectionner l'équipe principale par défaut
        const mainTeam = managedTeams.find(team => team.isMainTeam);
        if (mainTeam) {
          setSelectedTeamId(mainTeam.id);
        } else if (managedTeams.length > 0) {
          setSelectedTeamId(managedTeams[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement équipes disponibles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (event) => {
    const newTeamId = event.target.value;
    setSelectedTeamId(newTeamId);
    
    const selectedTeam = availableTeams.find(team => team.id === newTeamId);
    if (onTeamChange && selectedTeam) {
      onTeamChange(selectedTeam);
    }
  };

  const getTeamIcon = (team) => {
    if (currentUser.role === 'SUPER_ADMIN') {
      return <StarIcon sx={{ color: 'gold' }} />;
    }
    if (team.isMainTeam) {
      return <GroupIcon sx={{ color: 'primary.main' }} />;
    }
    if (team.isDelegated) {
      return <DelegateIcon sx={{ color: 'secondary.main' }} />;
    }
    return <GroupIcon />;
  };

  const getTeamLabel = (team) => {
    if (currentUser.role === 'SUPER_ADMIN') {
      return 'Super Admin';
    }
    if (team.isMainTeam) {
      return team.isAssistant ? 'Admin Adjoint' : 'Admin Principal';
    }
    if (team.isDelegated) {
      return 'Délégation';
    }
    return 'Équipe';
  };

  if (loading || availableTeams.length === 0) {
    return null;
  }

  // Si une seule équipe ET que l'utilisateur n'est pas SUPER_ADMIN, afficher juste le nom sans sélecteur
  // Pour les SUPER_ADMIN, toujours afficher le sélecteur même avec une équipe
  if (availableTeams.length === 1 && currentUser.role !== 'SUPER_ADMIN') {
    const team = availableTeams[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ 
          width: 24, 
          height: 24, 
          bgcolor: team.color || 'primary.main',
          fontSize: '0.7rem'
        }}>
          {team.name.charAt(0)}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {team.name}
        </Typography>
        <Chip 
          label={getTeamLabel(team)}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.6rem' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Équipe :
      </Typography>
      <FormControl size="small" sx={{ minWidth: 320 }}>
        <Select
          value={selectedTeamId || ''}
          onChange={handleTeamChange}
          variant="outlined"
          MenuProps={{
            PaperProps: {
              sx: {
                maxWidth: 450,
                minWidth: 320,
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              }
            }
          }}
          renderValue={(selectedTeamId) => {
            const selectedTeam = availableTeams.find(team => team.id === selectedTeamId);
            if (!selectedTeam) return '';
            
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Avatar sx={{ 
                  width: 20, 
                  height: 20, 
                  bgcolor: selectedTeam.color || 'primary.main',
                  fontSize: '0.65rem'
                }}>
                  {selectedTeam.name.charAt(0)}
                </Avatar>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {selectedTeam.name}
                </Typography>
                <Chip 
                  label={getTeamLabel(selectedTeam)}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    height: 18, 
                    fontSize: '0.6rem',
                    flexShrink: 0
                  }}
                />
              </Box>
            );
          }}
          sx={{ 
            height: 36,
            '& .MuiSelect-select': {
              py: 0.5,
              pr: 4
            }
          }}
        >
          {availableTeams.map((team) => (
            <MenuItem 
              key={team.id} 
              value={team.id}
              sx={{ 
                minHeight: 56,
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}
            >
              <Avatar sx={{ 
                width: 28, 
                height: 28, 
                bgcolor: team.color || 'primary.main',
                fontSize: '0.75rem',
                mt: 0.25
              }}>
                {team.name.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {team.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  {getTeamIcon(team)}
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {getTeamLabel(team)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TeamSelector;